import { put, select, all, fork, takeEvery, takeLatest, call } from 'redux-saga/effects';
import { isEmpty } from 'lodash-es';

import { sourceMappings, getUserProfile } from './getters';
import { CHECK_USER_AUTH_SUCCESS, LOGOUT, fetchUserProfile, FETCH_USER_PROFILE_SUCCESS } from './actions';
import Cookies from 'js-cookie';
import { generateLogoutAuthUrl } from 'shared/utils';
import { DefaultHeadersObject, getCsrfToken, IS_LOGGED_IN_LOCALSTORAGE_KEY } from 'shared/utils/request-utils';
import { fetchSourceMappings } from '../source/actions';
import * as Sentry from '@sentry/browser';
import { Enclave } from '../../shared/types/enclave';
import { LIGHT_THEME, THEME_LOCAL_STORAGE_KEY } from 'shared/constants';

// Watcher Sagas
function* watchForSuccessfulAuth() {
    yield takeLatest(CHECK_USER_AUTH_SUCCESS, fetchProfileOnSuccessfulAuth);
}

function* watchForFetchProfileSuccess() {
    yield takeLatest(FETCH_USER_PROFILE_SUCCESS, initSentryOnFetchProfileSuccess);
}

function* watchForLogout() {
    yield takeEvery(LOGOUT, logout);
}

// Worker Sagas
function* initSentryOnFetchProfileSuccess() {
    // Initialize Sentry error tracking
    const currentUserProfile = yield select(getUserProfile);
    const theme = currentUserProfile.preferences && currentUserProfile.preferences.themeMode &&
        currentUserProfile.preferences.themeMode !== 'NONE' ? currentUserProfile.preferences.themeMode : LIGHT_THEME;

    // We store the user's preferred theme or the default one in the localStorage.
    yield call([localStorage, 'setItem'], THEME_LOCAL_STORAGE_KEY, theme);

    const userSentryInfo = {
        id: currentUserProfile.guid,
        email: currentUserProfile.email,
        firstName: currentUserProfile.firstName,
        lastName: currentUserProfile.lastName,
        isTrialCompany: currentUserProfile.company.trialCompany,
        enclaveInboxLimit: currentUserProfile.company.maxIngestEmailsCount,
        firstVisitDate: currentUserProfile.activationDate
      };

      // Additional context for debugging
      Sentry.configureScope(scope => {
        scope.setUser(userSentryInfo);
        scope.setExtra(
          'enclaves',
          currentUserProfile.enclaves.map((enc: Enclave) => ({ name: enc.name }))
        );
        scope.setExtra('user_permissions', currentUserProfile.role.permissions);
        scope.setExtra('role', currentUserProfile.role.name);
      });

    yield true;
}
function* fetchProfileOnSuccessfulAuth() {
    const currentUserProfile = yield select(getUserProfile);
    const currentSourceMappings = yield select(sourceMappings);

    // Set XSRF for current session
    const xsrfToken = getCsrfToken();
    DefaultHeadersObject.xsrf(xsrfToken);

    localStorage.setItem(IS_LOGGED_IN_LOCALSTORAGE_KEY, 'true');

    if (isEmpty(currentUserProfile)) {
        yield put(fetchUserProfile());
    }

    if (!currentSourceMappings) {
        yield put(fetchSourceMappings());
    }

    yield true;
}
function* logout() {
    // Remove any auth identifiers
    Cookies.remove('XSRF-TOKEN');
    localStorage.removeItem(IS_LOGGED_IN_LOCALSTORAGE_KEY);
    DefaultHeadersObject.xsrf('');

    // Additional Logout Logic Here
    window.location.href = generateLogoutAuthUrl();

    yield true;
}

// Full export of all watcher sagas for Root Saga, also they are capitalized :D
export default function* UserSagas() {
    yield all([fork(watchForSuccessfulAuth), fork(watchForLogout), fork(watchForFetchProfileSuccess)]);
}
